import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { track } from '@/lib/analytics';
import {
  FiSettings,
  FiDownload,
  FiGrid,
  FiBook,
  FiHeart,
  FiShoppingBag,
  FiActivity,
  FiShoppingCart,
  FiTruck,
  FiUsers,
  FiFilm,
  FiMoreHorizontal,
  FiImage,
  FiFilter,
  FiFile,
} from 'react-icons/fi';
import { FaGavel } from 'react-icons/fa';
import { Card } from '@/components';
import { ActivationChecklistCard } from '@/components/ActivationChecklistCard';
import {
  ExpenseFilterModal,
  buildExpenseFilterQuery,
  countActiveExpenseFilters,
  EMPTY_EXPENSE_FILTERS,
  type ExpenseFilterState,
} from '@/components/ExpenseFilterModal';
import { apiJson, apiFetch, ApiError } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import { formatMoneyAbsolute, formatShortDate } from '@/lib/utils';
import type {
  AccountingSummary,
  ExpenseListResponse,
  ExpenseCategory,
  ExpenseCategoryListResponse,
  PaymentListResponse,
  PaymentMethod,
  PaymentMethodListResponse,
  UserResponse,
  HouseholdResponse,
  UnifiedItem,
} from '@/lib/types';

// Icon component map for categories
const categoryIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  general: FiGrid,
  school: FiBook,
  medical: FiHeart,
  clothing: FiShoppingBag,
  activities: FiActivity,
  groceries: FiShoppingCart,
  transportation: FiTruck,
  childcare: FiUsers,
  entertainment: FiFilm,
  other: FiMoreHorizontal,
};

function ExpensesPage() {
  const { getToken } = useAuth();
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [expenseFilters, setExpenseFilters] = useState<ExpenseFilterState>(
    EMPTY_EXPENSE_FILTERS
  );

  const filterQueryString = useMemo(
    () => buildExpenseFilterQuery(expenseFilters),
    [expenseFilters]
  );
  const activeFilterCount = useMemo(
    () => countActiveExpenseFilters(expenseFilters),
    [expenseFilters]
  );

  // Track screen view
  useEffect(() => {
    track('Expenses Screen');
  }, []);

  const { currentHouseholdId } = useHouseholds();

  // Fetch accounting summary
  const summaryQuery = useQuery({
    queryKey: ['accounting', 'summary', currentHouseholdId],
    queryFn: () =>
      apiJson<AccountingSummary>(getToken, '/v1/accounting/summary'),
  });

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  // Fetch expenses
  const expensesQuery = useQuery({
    queryKey: ['expenses', currentHouseholdId, filterQueryString],
    queryFn: async () => {
      try {
        return await apiJson<ExpenseListResponse>(
          getToken,
          `/v1/expenses${filterQueryString}`
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { expenses: [] };
        }
        throw err;
      }
    },
  });

  // Fetch payments
  const paymentsQuery = useQuery({
    queryKey: ['payments', currentHouseholdId],
    queryFn: async () => {
      try {
        return await apiJson<PaymentListResponse>(getToken, '/v1/payments');
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { payments: [] };
        }
        throw err;
      }
    },
  });

  // Fetch categories
  const categoriesQuery = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () =>
      apiJson<ExpenseCategoryListResponse>(getToken, '/v1/expense-categories'),
  });

  // Fetch payment methods
  const paymentMethodsQuery = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () =>
      apiJson<PaymentMethodListResponse>(getToken, '/v1/payment-methods'),
  });

  const owedCents = summaryQuery.data?.amount_owed_cents ?? 0;
  const currentUser = userQuery.data;
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];
  const expenses = expensesQuery.data?.expenses ?? [];
  const payments = paymentsQuery.data?.payments ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const paymentMethods = paymentMethodsQuery.data?.payment_methods ?? [];

  // Combine and sort transactions
  const unifiedItems: UnifiedItem[] = [
    ...expenses.map((expense) => ({ type: 'expense' as const, data: expense })),
    ...payments.map((payment) => ({ type: 'payment' as const, data: payment })),
  ].sort((a, b) => {
    const dateA =
      a.type === 'expense' ? a.data.purchase_date : a.data.payment_date;
    const dateB =
      b.type === 'expense' ? b.data.purchase_date : b.data.payment_date;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  const isLoading =
    expensesQuery.isLoading ||
    paymentsQuery.isLoading ||
    categoriesQuery.isLoading;
  const isError = expensesQuery.isError || paymentsQuery.isError;

  const handleExport = async () => {
    try {
      const response = await apiFetch(getToken, '/v1/export/accounting', {
        headers: { Accept: 'text/csv' },
      });
      if (!response.ok) throw new Error('Export failed');
      const csvText = await response.text();
      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'accounting-export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const getCategory = (categoryId: string): ExpenseCategory | undefined => {
    return categories.find((c) => c.expense_category_id === categoryId);
  };

  const getPaymentMethod = (methodId: string): PaymentMethod | undefined => {
    return paymentMethods.find((m) => m.payment_method_id === methodId);
  };

  const getCategoryIconComponent = (categoryId: string) => {
    return categoryIcons[categoryId] || FiGrid;
  };

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Mobile Header - hidden on desktop */}
      <header className='lg:hidden bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <div className='flex items-center justify-between'>
            <Link
              to='/app/settings'
              className='p-2 hover:bg-gray-100 rounded-full transition-colors'
            >
              <FiSettings className='w-5 h-5 text-primary' />
            </Link>
            <h1
              className='text-2xl font-light text-primary'
              style={{
                fontFamily:
                  "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Harbor
            </h1>
            {firstHousehold && (
              <button
                onClick={handleExport}
                className='p-2 hover:bg-gray-100 rounded-full transition-colors'
              >
                <FiDownload className='w-5 h-5 text-primary' />
              </button>
            )}
            {!firstHousehold && <div className='w-9' />}
          </div>
        </div>
      </header>

      {/* Desktop Header - shown only on large screens */}
      <header className='hidden lg:block bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-8 py-4'>
          <div className='flex items-center justify-between'>
            <h1
              className='text-xl font-semibold text-gray-900'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Expenses
            </h1>
            {firstHousehold && (
              <button
                onClick={handleExport}
                className='flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-xl transition-colors font-semibold'
              >
                <FiDownload className='w-4 h-4' />
                Export
              </button>
            )}
          </div>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6 space-y-6'>
        <ActivationChecklistCard />

        {/* Hero Card */}
        <Card className='text-center lg:text-left'>
          <p className='text-sm font-semibold text-primary/70 mb-1'>
            Amount Owed
          </p>
          <p
            className='text-4xl font-bold text-gray-900'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {formatMoneyAbsolute(owedCents)}
          </p>
          <div className='flex gap-3 mt-5'>
            <Link
              to='/app/expenses/record'
              className='flex-1 bg-primary/10 text-gray-900 font-semibold py-3 px-4 rounded-2xl text-center hover:bg-primary/20 transition-colors'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Add Expense
            </Link>
            <Link
              to='/app/payments/record'
              search={{
                expense_id: undefined,
                prefill_amount_cents: undefined,
                prefill_paid_by: undefined,
              }}
              className='flex-1 bg-primary text-white font-semibold py-3 px-4 rounded-2xl text-center hover:bg-primary-hover transition-colors'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Add Payment
            </Link>
          </div>
        </Card>

        {/* Transactions List */}
        <div>
          <div className='flex items-center justify-between mb-3'>
            <h2
              className='text-lg font-bold text-gray-900'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              All transactions
            </h2>
            <button
              type='button'
              onClick={() => {
                track('Expense Filter Opened');
                setFilterModalVisible(true);
              }}
              className='relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-primary font-semibold hover:bg-primary/10 transition-colors text-sm'
            >
              <FiFilter className='w-4 h-4' />
              Filter
              {activeFilterCount > 0 && (
                <span className='absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center px-1'>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {isLoading ? (
            <p className='text-gray-500 text-center py-8'>Loading...</p>
          ) : isError ? (
            <p className='text-gray-500 text-center py-8'>
              Couldn't load transactions.
            </p>
          ) : unifiedItems.length === 0 ? (
            <p className='text-gray-500 text-center py-8'>
              No transactions yet.
            </p>
          ) : (
            <div className='space-y-3'>
              {unifiedItems.map((item) => {
                if (item.type === 'expense') {
                  const expense = item.data;
                  const category = getCategory(expense.category);
                  const categoryColor = category?.color ?? '#018B9C';
                  const categoryName = category?.name ?? expense.category;
                  const IconComponent = getCategoryIconComponent(
                    expense.category
                  );
                  const hasDispute = (expense.disputes ?? []).length > 0;
                  const hasAttachments = (expense.attachments ?? []).length > 0;
                  const hasLinkedPayments = (expense.linked_payments ?? []).length > 0;
                  const hasPdfAttachment = (expense.attachments ?? []).some(
                    (a) =>
                      a.attachment_type === 'application/pdf' ||
                      a.name.toLowerCase().endsWith('.pdf')
                  );

                  // Calculate split display
                  const userSplit = currentUser
                    ? expense.splits?.find(
                        (s) => s.split_for_user_id === currentUser.id
                      )
                    : undefined;
                  let splitDisplay: string | undefined;
                  if (
                    expense.splits &&
                    expense.splits.length > 0 &&
                    userSplit
                  ) {
                    const userPercent = userSplit.amount_percent;
                    const otherPercent = expense.splits
                      .filter((s) => s.split_for_user_id !== currentUser?.id)
                      .reduce((sum, s) => sum + s.amount_percent, 0);
                    splitDisplay = `${userPercent}% - ${otherPercent}%`;
                  }

                  return (
                    <Link
                      key={`expense-${expense.id}`}
                      to='/app/expenses/$id'
                      params={{ id: String(expense.id) }}
                      className='block'
                    >
                      <Card className='flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer'>
                        <div
                          className='w-10 h-10 rounded-full flex items-center justify-center relative'
                          style={{ backgroundColor: categoryColor }}
                        >
                          <IconComponent className='w-5 h-5 text-white' />
                          {hasAttachments && (
                            <div className='absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-2 border-white'>
                              {hasPdfAttachment ? (
                                <FiFile className='w-2.5 h-2.5 text-white' />
                              ) : (
                                <FiImage className='w-2.5 h-2.5 text-white' />
                              )}
                            </div>
                          )}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='font-bold text-gray-900 truncate'>
                            {expense.name}
                          </p>
                          <div className='flex items-center gap-1.5 mt-1'>
                            <p className='text-xs text-gray-400'>
                              {formatShortDate(expense.purchase_date)} •{' '}
                              {categoryName}
                            </p>
                            {hasDispute && (
                              <FaGavel className='w-3 h-3 text-amber-500' />
                            )}
                          </div>
                        </div>
                        <div className='text-right'>
                          <p className='font-extrabold text-gray-900'>
                            {formatMoneyAbsolute(expense.amount_cents)}
                          </p>
                          {expense.is_fully_paid_back ? (
                            <p className='text-xs font-semibold text-primary mt-1'>
                              Paid Back
                            </p>
                          ) : splitDisplay ? (
                            <div className='flex items-center justify-end gap-1 mt-1'>
                              {hasLinkedPayments && (
                                <span className='inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold'>
                                  $
                                </span>
                              )}
                              <p className='text-xs text-gray-400'>{splitDisplay}</p>
                            </div>
                          ) : null}
                        </div>
                      </Card>
                    </Link>
                  );
                } else {
                  const payment = item.data;
                  const method = getPaymentMethod(payment.payment_method);
                  const methodColor = method?.color ?? '#9B59B6';
                  const methodName = method?.name ?? payment.payment_method;
                  const hasDispute = (payment.disputes ?? []).length > 0;

                  return (
                    <Link
                      key={`payment-${payment.id}`}
                      to='/app/payments/$id'
                      params={{ id: String(payment.id) }}
                      className='block'
                    >
                      <Card className='flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer'>
                        <div
                          className='w-10 h-10 rounded-full flex items-center justify-center'
                          style={{ backgroundColor: methodColor }}
                        >
                          <span className='text-white font-bold text-lg'>
                            $
                          </span>
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='font-bold text-gray-900'>Payment</p>
                          <div className='flex items-center gap-1.5 mt-1'>
                            <p className='text-xs text-gray-400'>
                              {formatShortDate(payment.payment_date)} •{' '}
                              {methodName}
                            </p>
                            {hasDispute && (
                              <FaGavel className='w-3 h-3 text-amber-500' />
                            )}
                          </div>
                        </div>
                        <div className='text-right'>
                          <p className='font-extrabold text-gray-900'>
                            {formatMoneyAbsolute(payment.amount_cents)}
                          </p>
                        </div>
                      </Card>
                    </Link>
                  );
                }
              })}
            </div>
          )}
        </div>
      </div>

      <ExpenseFilterModal
        open={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filters={expenseFilters}
        onChange={setExpenseFilters}
        categories={categories}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/expenses/')({
  component: ExpensesPage,
});
